import type { StateCreator } from 'zustand';
import type { SliceMutators } from '~/store/mutators';
import type { LayoutMode } from '~/types/pipeline';

export type AtlasSlice = {
  atlas: {
    exporting: boolean;
    lastError: string | null;
    layoutMode: LayoutMode;
  };
  setExporting: (b: boolean) => void;
  setExportError: (e: string | null) => void;
  setLayoutMode: (mode: LayoutMode) => void;
  setLayoutGridSize: (rows: number, cols: number) => void;
  setLayoutFill: (fill: 'row-major' | 'col-major') => void;
  setFreePosition: (frameIndex: number, row: number, col: number) => void;
  resetAtlas: () => void;
  resetAtlasLayout: () => void;
};

const INITIAL_ATLAS = {
  exporting: false,
  lastError: null,
  layoutMode: { kind: 'sequence' } as LayoutMode,
} satisfies AtlasSlice['atlas'];

export const createAtlasSlice: StateCreator<AtlasSlice, SliceMutators, [], AtlasSlice> = (set) => ({
  atlas: { ...INITIAL_ATLAS },
  setExporting: (b) =>
    set((s) => {
      s.atlas.exporting = b;
    }),
  setExportError: (e) =>
    set((s) => {
      s.atlas.lastError = e;
    }),
  setLayoutMode: (mode) =>
    set((s) => {
      s.atlas.layoutMode = mode;
    }),
  setLayoutGridSize: (rows, cols) =>
    set((s) => {
      if (s.atlas.layoutMode.kind === 'grid') {
        s.atlas.layoutMode = { ...s.atlas.layoutMode, rows, cols };
      } else {
        s.atlas.layoutMode = { kind: 'grid', rows, cols, fill: 'row-major' };
      }
    }),
  setLayoutFill: (fill) =>
    set((s) => {
      if (s.atlas.layoutMode.kind === 'grid') {
        s.atlas.layoutMode = { ...s.atlas.layoutMode, fill };
      }
    }),
  setFreePosition: (frameIndex, row, col) =>
    set((s) => {
      if (s.atlas.layoutMode.kind === 'free') {
        s.atlas.layoutMode = {
          ...s.atlas.layoutMode,
          positions: { ...s.atlas.layoutMode.positions, [frameIndex]: { row, col } },
        };
      }
    }),
  resetAtlas: () =>
    set((s) => {
      s.atlas = { ...INITIAL_ATLAS };
    }),
  resetAtlasLayout: () =>
    set((s) => {
      s.atlas.layoutMode = { kind: 'sequence' };
    }),
});
